// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "@reach/router";
import { Button, Divider, Form, message, Modal, Popconfirm } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { SaveOutlined, StopOutlined } from "@ant-design/icons/lib";
import { requestCreateUser } from "../../../../state/userManagement/UserManagementEffects";
import { addUser } from "../../../../state/userManagement/UserManagementEvents";
import {
  AuthentifiedUser,
  UserCreateRequestDTO
} from "../../../../api/backend-openapi";
import UserSettingsFormSection from "../details/UserSettingsFormSection";
import { useStore } from "effector-react";
import { authentifiedUserStore } from "../../../../state/auth/AuthStore";

const { confirm } = Modal;

const AddUserForm: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [branch, setBranch] = useState<string | undefined>(undefined);
  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirm"),
        content: t("add_users.modal.description"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    return requestCreateUser.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addUser(result.data);
        navigate("/users");
        message.success(t("add_users.success"));
      } else {
        message.error(result.errorMessage);
      }
    });
  });

  const handleSubmit = (values: any) => {
    confirmationPopup().then((confirmed: boolean) => {
      if (confirmed && authentifiedUser && authentifiedUser.email) {
        const userToAdd: UserCreateRequestDTO = {
          email: values.email,
          profiles: values.profiles,
          status: values.status,
          branch: values.branch,
          newsThemeIds: values.newsThemeIds,
          creator: authentifiedUser.email
        };

        requestCreateUser(userToAdd);
        setButtonLoading(true);
      }
    });
  };

  const handleSave = (values: any) => {
    handleSubmit(values);
  };

  const onFinishFailed = ({ errorFields }: any) => {
    message.error(t("global.on_finish_failed"));
    form.scrollToField(errorFields[0].name);
  };

  return (
    <Form
      {...formItemBlockLayout}
      onFinish={handleSave}
      onFinishFailed={onFinishFailed}
      form={form}
    >
      <div className="form-first-item-sticky-top-container">
        <div className="form-first-item-sticky-top-content">
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("user.user_actions")}</h3>
          </Divider>
          <Form.Item className="text-center mb-0">
            <Popconfirm
              title={t("add_users.cancel_confirmation")}
              okText={t("global.yes")}
              cancelText={t("global.no")}
              onConfirm={() => {
                navigate("/users");
                form.resetFields();
              }}
              placement="top"
              icon={<QuestionCircleOutlined />}
            >
              <Button htmlType="reset" className="m-2">
                <StopOutlined /> {t("add_users.cancel")}
              </Button>
            </Popconfirm>
            <Button
              className="btn-primary m-2"
              htmlType="submit"
              loading={buttonLoading}
            >
              <SaveOutlined /> {t("global.save")}
            </Button>
          </Form.Item>
        </div>
      </div>
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("user.settings")}</h3>
      </Divider>
      <UserSettingsFormSection
        showRequired={true}
        editMode={true}
        branch={branch}
        setBranch={setBranch}
        resetNewsTheme={() => {
          form.setFields([
            {
              name: "newsThemeId",
              value: null
            }
          ]);
        }}
      />
    </Form>
  );
};

export default AddUserForm;
